import type {
  Customer,
  CustomerAddress,
  PatchAdminV1StoresStoreIdCustomersIdAddressesAddressIdBody,
  PatchAdminV1StoresStoreIdCustomersIdBody,
  PostAdminV1StoresStoreIdCustomersBody,
  PostAdminV1StoresStoreIdCustomersIdAddressesBody,
  PostAdminV1StoresStoreIdCustomersIdInviteBody,
} from '@nucleus-commerce/api-client/admin';

import type {
  CustomerAddressFormInput,
  CustomerAddressFormOutput,
} from '../schemas/customer-address-schema';
import type {
  AccountEmailFormOutput,
  CustomerCreateFormInput,
  CustomerCreateFormOutput,
  CustomerProfileFormInput,
  CustomerProfileFormOutput,
} from '../schemas/customer-form-schema';

export function emptyCustomerForm(): CustomerCreateFormInput {
  return {
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    acceptsMarketing: false,
    tags: [],
    notes: '',
  };
}

export function toCustomerCreate(
  values: CustomerCreateFormOutput,
): PostAdminV1StoresStoreIdCustomersBody {
  return {
    email: values.email,
    ...(values.firstName ? { firstName: values.firstName } : {}),
    ...(values.lastName ? { lastName: values.lastName } : {}),
    ...(values.phone ? { phone: values.phone } : {}),
    acceptsMarketing: values.acceptsMarketing,
    ...(values.tags.length > 0 ? { tags: values.tags } : {}),
    ...(values.notes ? { notes: values.notes } : {}),
  };
}

export function fromCustomer(customer: Customer): CustomerProfileFormInput {
  return {
    firstName: customer.firstName ?? '',
    lastName: customer.lastName ?? '',
    phone: customer.phone ?? '',
    acceptsMarketing: customer.acceptsMarketing,
    savedRingSize: customer.savedRingSize ?? '',
    status: customer.status,
  };
}

const orNull = (value: string) => (value === '' ? null : value);

/** Status goes out only when it changed, and never as `guest`, which the API does not accept. */
export function toCustomerUpdate(
  values: CustomerProfileFormOutput,
  current: Customer,
): PatchAdminV1StoresStoreIdCustomersIdBody {
  return {
    firstName: orNull(values.firstName),
    lastName: orNull(values.lastName),
    phone: orNull(values.phone),
    acceptsMarketing: values.acceptsMarketing,
    savedRingSize: orNull(values.savedRingSize),
    ...(values.status !== current.status && values.status !== 'guest'
      ? { status: values.status }
      : {}),
  };
}

export function emptyAddressForm(countryCode: string, first: boolean): CustomerAddressFormInput {
  return {
    label: '',
    firstName: '',
    lastName: '',
    company: '',
    line1: '',
    line2: '',
    city: '',
    region: '',
    postalCode: '',
    countryCode,
    phone: '',
    isDefaultShipping: first,
    isDefaultBilling: first,
  };
}

export function fromAddress(address: CustomerAddress): CustomerAddressFormInput {
  return {
    label: address.label ?? '',
    firstName: address.firstName ?? '',
    lastName: address.lastName ?? '',
    company: address.company ?? '',
    line1: address.line1,
    line2: address.line2 ?? '',
    city: address.city,
    region: address.region ?? '',
    postalCode: address.postalCode ?? '',
    countryCode: address.countryCode,
    phone: address.phone ?? '',
    isDefaultShipping: address.isDefaultShipping,
    isDefaultBilling: address.isDefaultBilling,
  };
}

/** Every field goes out on create and edit alike, so clearing a field clears it on the server. */
export function toAddressBody(
  values: CustomerAddressFormOutput,
): PostAdminV1StoresStoreIdCustomersIdAddressesBody &
  PatchAdminV1StoresStoreIdCustomersIdAddressesAddressIdBody {
  return {
    label: orNull(values.label),
    firstName: orNull(values.firstName),
    lastName: orNull(values.lastName),
    company: orNull(values.company),
    line1: values.line1,
    line2: orNull(values.line2),
    city: values.city,
    region: orNull(values.region),
    postalCode: orNull(values.postalCode),
    countryCode: values.countryCode,
    phone: orNull(values.phone),
    isDefaultShipping: values.isDefaultShipping,
    isDefaultBilling: values.isDefaultBilling,
  };
}

export function toAccountEmailBody(
  values: AccountEmailFormOutput,
): PostAdminV1StoresStoreIdCustomersIdInviteBody {
  return values.resetUrl ? { resetUrl: values.resetUrl } : {};
}
